import { ROUTES } from "../constants/api";
import axios from "axios";
import AuthApi from "./auth";
import {
  HandleErrorResponse,
  HandleResponse,
  HandleNewErrorResponse,
  HandleNewResponse,
} from "./handlers";
import { CachedStates } from "../types/general";
import { LOCAL_STORAGE_KEYS } from "../constants/enums";

/**
 * Class for static methods handling a user's card(s)
 */
class CatalogApi {
  static US_STATES_TTL_MS = 1000 * 60 * 60 * 24 * 60; // 60 days

  static async GetPasswordQuestions() {
    try {
      let response = await axios.get(ROUTES.CATALOG.GET_PASSWORD_QUESTIONS);

      return HandleNewResponse(response);
    } catch (error) {
      return HandleNewErrorResponse(error);
    }
  }

  /**
   * Get a site document (disclosure, electronic communications agreement) as
   * its raw binary response.
   *
   * Anonymous on purpose: these documents are linked from enrollment, before
   * the user has an account to authenticate with.
   */
  static async GetSiteDocument(id: string) {
    return await axios.get<Blob>(ROUTES.CATALOG.GET_SITE_DOCUMENT(id), {
      responseType: "blob",
    });
  }

  /**
   * Get all supported countries
   * country data
   */
  static async GetCountries() {
    let url = ROUTES.CATALOG.COUNTRIES;

    try {
      let response = await axios.get(url, {
        headers: AuthApi.getAuthorizationHeaders(),
      });
      return HandleResponse(response);
    } catch (err) {
      return HandleErrorResponse(err);
    }
  }

  /**
   * Get all supported stateProvinces by countryIso (ISO 2 Code)
   *  stateProvince data
   */
  static async GetStateProvinces(countryIso) {
    const iso = String(countryIso || "").toUpperCase();
    if (!/^[A-Z]{2}$/.test(iso)) {
      return HandleErrorResponse({ response: { data: { message: "Invalid countryIso" } } });
    }

    let url = ROUTES.CATALOG.STATE_PROVINCES(iso);
    try {
      let response = await axios.get(url, {
        headers: AuthApi.getAuthorizationHeaders(),
      });
      return HandleResponse(response);
    } catch (err) {
      return HandleErrorResponse(err);
    }
  }

  static async getUSStates() {
    try {
      // 1️⃣ Check localStorage
      const cachedRaw = localStorage.getItem(LOCAL_STORAGE_KEYS.US_STATES);

      if (cachedRaw) {
        const cached: CachedStates = JSON.parse(cachedRaw);

        const isExpired =
          Date.now() - cached.timestamp > CatalogApi.US_STATES_TTL_MS;

        if (!isExpired && cached.data?.length) {
          return cached.data;
        }
      }

      // 2️⃣ Fetch fresh data
      const response = await CatalogApi.GetStateProvinces("US");
      const states = response.stateProvinces;

      // 3️⃣ Save to localStorage
      const payload: CachedStates = {
        data: states,
        timestamp: Date.now(),
      };

      localStorage.setItem(
        LOCAL_STORAGE_KEYS.US_STATES,
        JSON.stringify(payload)
      );

      return states;
    } catch (err) {
      throw err;
    }
  };
}

export default CatalogApi;
